// Editor for the headers attribute: key/value rows with an encrypted flag.
// Reports blank entries back so the screen can block submission.

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Button from '@decisiv/ui-components/lib/components/Button';
import Checkbox from '@decisiv/ui-components/lib/components/Checkbox';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import MinusCircle from '@decisiv/iconix/lib/components/MinusCircle';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

import {
  isLastEntryBlank,
  partOfHeaderInputName,
  toEditableList,
} from '../../core/headers';
import { withDefaultTexts } from '../texts';
import { descriptorShape } from './fieldPropTypes';

const ROW_STYLE = {
  whiteSpace: 'nowrap',
  gap: '1.25rem',
  margin: '-1px',
  border: '1px solid var(--if-border)',
  padding: '0.625rem',
};

const ADD_ROW_STYLE = {
  border: '1px solid var(--if-border)',
  margin: '-1px',
  gap: '1.25rem',
  padding: '0.625rem',
};

const KEY_MAX_LENGTH = 50;
const VALUE_MAX_LENGTH = 500;

const HeadersInput = ({
  descriptor,
  value = [],
  errors = {},
  readOnly = true,
  texts = undefined,
  encryptedPlaceholder = '',
  restoreSecureOnErase = false,
  onChange,
  onAdd,
  onRemove,
  onValidate,
}) => {
  const t = withDefaultTexts(texts);
  const headers = toEditableList(value);

  // What each encrypted row held when the form opened. Clearing such a row would
  // otherwise persist an empty string over the stored ciphertext.
  const [storedSecureValues] = useState(() =>
    toEditableList(value).map((entry) => (entry[2] ? entry[1] : null)),
  );

  // A stored encrypted value is a long ciphertext blob; showing it back would be
  // noise, so the field stays empty until the user types a replacement.
  const displayedValue = (item) => {
    if (!item[2]) return item[1];
    if (readOnly) return encryptedPlaceholder ? '' : t.encrypted;
    return item[3] ? item[1] : '';
  };

  // Only shown where the field is blank because the value is encrypted, so the
  // row does not read as empty.
  const placeholderFor = (item) =>
    encryptedPlaceholder && item[2] && !item[3] ? encryptedPlaceholder : '';

  // A screen with more than one headers attribute can scope its row errors with
  // '<field>.key-0'; the bare key stays supported for single-field screens.
  const errorFor = (part, index) =>
    errors[`${descriptor.name}.${part}-${index}`] ?? errors[`${part}-${index}`];

  const handleChange = (e, item, index) => {
    const inputName = e.target.name;
    const part = partOfHeaderInputName(inputName);

    const erasedSecure =
      restoreSecureOnErase &&
      part === 'value' &&
      e.target.value === '' &&
      storedSecureValues[index] != null;

    onChange(descriptor.name, {
      index,
      item,
      part,
      value: erasedSecure ? storedSecureValues[index] : e.target.value,
      checked: e.target.checked,
    });

    onValidate(inputName, isEmpty(String(e.target.value ?? '').trim()));
  };

  return (
    <>
      {headers.map((item, index) => (
        <Flex
          // Rows are positional: an entry has no identity beyond its index.
          // eslint-disable-next-line react/no-array-index-key
          key={`${descriptor.name}-row-${index}`}
          justifyContent="flex-start"
          alignItems="center"
          style={ROW_STYLE}
        >
          <Button
            aria-label={t.removeEntry}
            variant="ghost"
            icon={MinusCircle}
            disabled={readOnly}
            onClick={() => onRemove(descriptor.name, index)}
          />
          <TextField
            required
            value={item[0]}
            label={t.headerKey}
            name={`key-${index}`}
            style={{ marginTop: '0.5rem' }}
            maxLength={KEY_MAX_LENGTH}
            disabled={readOnly}
            showCharacterCount={isEmpty(errorFor('key', index))}
            warningMessage={errorFor('key', index)}
            onChange={(e) => handleChange(e, item, index)}
          />
          <TextField
            required
            value={displayedValue(item)}
            placeholder={placeholderFor(item)}
            label={t.headerValue}
            name={`value-${index}`}
            style={{ marginTop: '0.5rem' }}
            maxLength={VALUE_MAX_LENGTH}
            disabled={readOnly}
            showCharacterCount={isEmpty(errorFor('value', index))}
            warningMessage={errorFor('value', index)}
            onChange={(e) => handleChange(e, item, index)}
          />
          <Checkbox
            disabled={readOnly}
            name={`secure-${index}`}
            label={t.encrypted}
            value="secure"
            checked={Boolean(item[2])}
            onChange={(e) => handleChange(e, item, index)}
          />
        </Flex>
      ))}
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        style={ADD_ROW_STYLE}
      >
        <Button
          aria-label={`${t.add} ${descriptor.name}`}
          text={`${t.add} ${descriptor.name}`}
          icon={PlusCircle}
          variant="ghost"
          disabled={readOnly || isLastEntryBlank(headers)}
          onClick={() => onAdd(descriptor.name)}
        />
      </Flex>
    </>
  );
};

HeadersInput.propTypes = {
  descriptor: descriptorShape.isRequired,
  // Legacy records may hold a bare string; the component normalizes it.
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
  ]),
  errors: PropTypes.objectOf(PropTypes.string),
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  texts: PropTypes.object,
  encryptedPlaceholder: PropTypes.string,
  restoreSecureOnErase: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};
export default HeadersInput;
